import { useEffect, useState } from "react";

// Resolve a stored logo path (e.g. "/api/files/...") to a fully-qualified URL.
export function resolveLogoUrl(url) {
  if (!url) return null;
  if (url.startsWith("/api/")) return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  return url;
}

const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr.result);
    fr.onerror = reject;
    fr.readAsDataURL(blob);
  });

const loadImg = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

const _cache = new Map();

// Fetch a logo and normalize it to a PNG data URL (avoids jsPDF format/webp issues + CORS taint).
// Returns { dataUrl, w, h } or null on any failure. Never throws. Cached per URL.
export async function loadLogoData(url) {
  const full = resolveLogoUrl(url);
  if (!full) return null;
  if (_cache.has(full)) return _cache.get(full);
  const task = (async () => {
    try {
      const res = await fetch(full, { mode: "cors" });
      if (!res.ok) return null;
      const blob = await res.blob();
      const dataUrl = await blobToDataUrl(blob);
      const img = await loadImg(dataUrl);
      const nw = img.naturalWidth || 1;
      const nh = img.naturalHeight || 1;
      // Downscale to a small chip-friendly size so embedded PDFs (and emailed roster attachments) stay lean.
      const MAX = 256;
      const scale = Math.min(1, MAX / Math.max(nw, nh));
      const w = Math.max(1, Math.round(nw * scale));
      const h = Math.max(1, Math.round(nh * scale));
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      canvas.getContext("2d").drawImage(img, 0, 0, w, h);
      return { dataUrl: canvas.toDataURL("image/png"), w, h };
    } catch {
      return null;
    }
  })();
  _cache.set(full, task);
  const result = await task;
  _cache.set(full, result); // swap the promise for the resolved value
  return result;
}

// Draw a dark-mode-safe white chip with the logo contained inside it.
export function drawLogoChip(doc, logo, x, y, size, pad = 4) {
  if (!logo || !logo.dataUrl) return;
  doc.setFillColor(255, 255, 255);
  doc.roundedRect(x, y, size, size, 4, 4, "F");
  const inner = size - pad * 2;
  const ratio = logo.w && logo.h ? logo.w / logo.h : 1;
  let w = inner;
  let h = inner;
  if (ratio > 1) h = inner / ratio;
  else w = inner * ratio;
  const ix = x + pad + (inner - w) / 2;
  const iy = y + pad + (inner - h) / 2;
  try {
    doc.addImage(logo.dataUrl, "PNG", ix, iy, w, h);
  } catch {
    /* ignore */
  }
}

// React hook: preloads the school logo into PNG data ({dataUrl,w,h}) for use in PDF builders.
export function useLogoData(url) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let alive = true;
    if (!url) {
      setData(null);
      return;
    }
    loadLogoData(url).then((d) => {
      if (alive) setData(d);
    });
    return () => {
      alive = false;
    };
  }, [url]);
  return data;
}

// Draw a big faint watermark centered (H+V) on the CURRENT page.
export function drawCenterWatermark(doc, wm, opacity = 0.08) {
  if (!wm || !wm.dataUrl) return;
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const ratio = wm.w && wm.h ? wm.w / wm.h : 2;
  let w = pageW * 0.6;
  let h = w / ratio;
  const maxH = pageH * 0.4;
  if (h > maxH) { h = maxH; w = h * ratio; }
  const x = (pageW - w) / 2;
  const y = (pageH - h) / 2;
  try {
    doc.saveGraphicsState();
    doc.setGState(new doc.GState({ opacity }));
    doc.addImage(wm.dataUrl, "PNG", x, y, w, h);
    doc.restoreGraphicsState();
  } catch (e) { /* watermark is best-effort */ }
}

// Athletic year (e.g. "2025-26") stamped on every non-academic PDF. Set once by SchoolProvider.
let _athleticYear = "";
export function setAthleticYear(v) { _athleticYear = (v || "").trim(); }
export function getAthleticYear() { return _athleticYear; }

function drawYearStamp(doc, year) {
  if (!year) return;
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.setTextColor(150, 150, 150);
  doc.text(`Athletic Year ${year}`, pageW - 32, pageH - 14, { align: "right" });
}

// Stamp the centered watermark (if any) + the athletic year on EVERY page (call right before save/print).
// opts: { opacity, skipYear, year }. A numeric 3rd arg is treated as legacy opacity.
export function stampWatermark(doc, wm, opts = {}) {
  const o = typeof opts === "number" ? { opacity: opts } : (opts || {});
  const opacity = o.opacity == null ? 0.08 : o.opacity;
  const year = o.year == null ? _athleticYear : o.year;
  const showYear = !o.skipYear && !!year;
  const hasWm = !!(wm && wm.dataUrl);
  if (!hasWm && !showYear) return;
  const pages = doc.getNumberOfPages();
  for (let p = 1; p <= pages; p++) {
    doc.setPage(p);
    if (hasWm) drawCenterWatermark(doc, wm, opacity);
    if (showYear) drawYearStamp(doc, year);
  }
  doc.setPage(pages);
}
